import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { AuthProvider } from '@/store/AuthContext'
import RoleBasedRedirect from '@/components/RoleBasedRedirect'
import LandingPage from '@/pages/LandingPage'
import InstitutionsPage from '@/pages/InstitutionsPage'
import UsersPage from '@/pages/UsersPage'
import TeachersPage from '@/pages/TeachersPage'
import StudentsPage from '@/pages/StudentsPage'
import ParentsPage from '@/pages/ParentsPage'
import DriversPage from '@/pages/DriversPage'
import OnboardPage from '@/pages/OnboardPage'
import UserOnboardingPage from '@/pages/UserOnboardingPage'
import LessonsPage from '@/pages/LessonsPage'
import SessionsPage from '@/pages/SessionsPage'
import FleetOverviewPage from '@/pages/FleetOverviewPage'
import RoutePlanningPage from '@/pages/RoutePlanningPage'
import TripsPage from '@/pages/TripsPage'
import GradesPage from '@/pages/GradesPage'
import StreamsPage from '@/pages/StreamsPage'
import GroupsPage from '@/pages/GroupsPage'
import SuperAdminsPage from '@/pages/SuperAdminsPage'
import AnalyticsPage from '@/pages/AnalyticsPage'
import AttendancePage from '@/pages/AttendancePage'
import LessonDetailPage from '@/pages/LessonDetailPage'
import SessionAttendanceDetailPage from '@/pages/SessionAttendanceDetailPage'
import SettingsPage from '@/pages/SettingsPage'
import SystemLogsPage from '@/pages/SystemLogsPage'
import RFIDTerminalsPage from '@/pages/RFIDTerminalsPage'
import RFIDCardsPage from '@/pages/RFIDCardsPage'
import ProfilePage from '@/pages/ProfilePage'
import TwoFactorSettingsPage from '@/pages/TwoFactorSettingsPage'
import SchoolAdminDashboard from '@/pages/SchoolAdminDashboard'
import SuperAdminDashboard from '@/pages/SuperAdminDashboard'
import TeacherDashboard from '@/pages/TeacherDashboard'
import DriverDashboard from '@/pages/DriverDashboard'
import StudentDashboard from '@/pages/StudentDashboard'
import ParentDashboard from '@/pages/ParentDashboard'
import DriverRoutesPage from '@/pages/DriverRoutesPage'
import DriverStudentsPage from '@/pages/DriverStudentsPage'
import NotFoundPage from '@/pages/NotFoundPage'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import LoginPage from './pages/LoginPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import DashboardLayout from '@/components/layouts/DashboardLayout'
import SecuritySetupGuard from '@/components/security/SecuritySetupGuard'

// GPS Management Pages
import GPSFleetPage from '@/pages/GPSFleetPage'
import GPSTrackingDashboard from '@/pages/GPSTrackingDashboard'
import GeofenceManagement from '@/pages/GeofenceManagement'
import VehicleControlCenter from '@/pages/VehicleControlCenter'
import GPSReportsPage from '@/pages/GPSReportsPage'
import TransportReportsPage from '@/pages/TransportReportsPage'
import ProtrackCredentialsPage from '@/pages/ProtrackCredentialsPage'

// Library Management Pages
import LibraryDashboardPage from '@/pages/LibraryDashboardPage'
import BooksPage from '@/pages/BooksPage'
import BorrowingsPage from '@/pages/BorrowingsPage'
import FinesPage from '@/pages/FinesPage'
import LibraryCategoriesPage from '@/pages/LibraryCategoriesPage'

// Payment Configuration Page
import PaymentConfigPage from '@/pages/schoolAdmin/PaymentConfigPage'

// Invoicing Module
import InvoicesPage from '@/pages/InvoicesPage'
import InvoiceDetailPage from '@/pages/InvoiceDetailPage'
import BillingManagementPage from '@/pages/BillingManagementPage'
import FeeGenerationPage from '@/pages/FeeGenerationPage'

// Meals Management Pages
import MealTypesPage from '@/pages/MealTypesPage'
import MealSessionsPage from '@/pages/MealSessionsPage'
import MealAttendancePage from '@/pages/MealAttendancePage'
import MealReportsPage from '@/pages/MealReportsPage'

// Analytics & Reports Pages
import SchoolAnalyticsPage from '@/pages/SchoolAnalyticsPage'
import AttendanceReportsPage from '@/pages/AttendanceReportsPage'
import PerformanceReportsPage from '@/pages/PerformanceReportsPage'

// Communication Module Pages
import { AnnouncementsPage, EventsPage, NotificationsPage } from '@/pages/communication'
import { NotificationProvider } from '@/store/NotificationContext'

// Wallet Management Pages
import {
  WalletDashboard as SchoolAdminWalletDashboard,
  TransactionsPage as SchoolAdminTransactionsPage,
  CreditWalletPage,
  DebitWalletPage,
} from '@/pages/schoolAdmin/wallet'
import {
  WalletDashboard as ParentWalletDashboard,
  TransactionsPage as ParentTransactionsPage,
  InvoicesPage as ParentInvoicesPage,
  CreditsPage as ParentCreditsPage,
  DebitsPage as ParentDebitsPage,
  TopUpPage as ParentTopUpPage,
  StudentWalletTopUpPage as ParentStudentWalletTopUpPage,
} from '@/pages/parent/wallet'
import {
  WalletDashboard as StudentWalletDashboard,
  TransactionsPage as StudentTransactionsPage,
  CreditsPage as StudentCreditsPage,
  DebitsPage as StudentDebitsPage,
} from '@/pages/student/wallet'

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return (
    <SecuritySetupGuard>
      <DashboardLayout>{children}</DashboardLayout>
    </SecuritySetupGuard>
  )
}

function AppRoutes() {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={user ? <RoleBasedRedirect /> : <LandingPage />} />
      <Route path="/login" element={user ? <RoleBasedRedirect /> : <LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* User onboarding route - requires authentication but bypasses SecuritySetupGuard */}
      <Route path="/onboard" element={
        user ? <UserOnboardingPage /> : <Navigate to="/login" replace />
      } />
      
      {/* Super Admin routes */}
      <Route path="/admin/dashboard" element={<ProtectedRoute><SuperAdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/institutions" element={<ProtectedRoute><InstitutionsPage /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/admin/groups" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
      <Route path="/admin/super-admins" element={<ProtectedRoute><SuperAdminsPage /></ProtectedRoute>} />
      <Route path="/admin/school-admins" element={<ProtectedRoute><SuperAdminsPage /></ProtectedRoute>} />
      <Route path="/admin/teachers" element={<ProtectedRoute><TeachersPage /></ProtectedRoute>} />
      <Route path="/admin/students" element={<ProtectedRoute><StudentsPage /></ProtectedRoute>} />
      <Route path="/admin/parents" element={<ProtectedRoute><ParentsPage /></ProtectedRoute>} />
      <Route path="/admin/drivers" element={<ProtectedRoute><DriversPage /></ProtectedRoute>} />
      <Route path="/admin/transport/drivers" element={<ProtectedRoute><DriversPage /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin/system/config" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin/system/logs" element={<ProtectedRoute><SystemLogsPage /></ProtectedRoute>} />
      <Route path="/admin/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/admin/2fa" element={<ProtectedRoute><TwoFactorSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
      <Route path="/admin/billing" element={<ProtectedRoute><BillingManagementPage /></ProtectedRoute>} />
      <Route path="/admin/finance/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
      <Route path="/admin/finance/billing" element={<ProtectedRoute><BillingManagementPage /></ProtectedRoute>} />

      {/* Communication Module - Super Admin */}
      <Route path="/admin/communication/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
      <Route path="/admin/communication/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
      <Route path="/admin/communication/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

      {/* School Admin routes */}
      <Route path="/schoolAdmin/dashboard" element={<ProtectedRoute><SchoolAdminDashboard /></ProtectedRoute>} />
      
      {/* User Management */}
      <Route path="/schoolAdmin/teachers" element={<ProtectedRoute><TeachersPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/students" element={<ProtectedRoute><StudentsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/parents" element={<ProtectedRoute><ParentsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/drivers" element={<ProtectedRoute><DriversPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/driver-management" element={<ProtectedRoute><DriversPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/onboard" element={<ProtectedRoute><OnboardPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/2fa" element={<ProtectedRoute><TwoFactorSettingsPage /></ProtectedRoute>} />
      
      {/* Academic Management */}
      <Route path="/schoolAdmin/grades" element={<ProtectedRoute><GradesPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/streams" element={<ProtectedRoute><StreamsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/lessons" element={<ProtectedRoute><LessonsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/sessions" element={<ProtectedRoute><SessionsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/classes" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/subjects" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/attendance" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/academic/attendance" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/timetables" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/calendar" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/grade-reports" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/exams" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      
      {/* Super Admin Academic Routes */}
      <Route path="/admin/academic/grades" element={<ProtectedRoute><GradesPage /></ProtectedRoute>} />
      <Route path="/admin/academic/streams" element={<ProtectedRoute><StreamsPage /></ProtectedRoute>} />
      <Route path="/admin/academic/lessons" element={<ProtectedRoute><LessonsPage /></ProtectedRoute>} />
      <Route path="/admin/academic/classes" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
      <Route path="/admin/academic/sessions" element={<ProtectedRoute><SessionsPage /></ProtectedRoute>} />
      <Route path="/admin/academic/attendance" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
      <Route path="/lesson-detail/:lessonId" element={<ProtectedRoute><LessonDetailPage /></ProtectedRoute>} />
      <Route path="/attendance-detail/:sessionId" element={<ProtectedRoute><SessionAttendanceDetailPage /></ProtectedRoute>} />
      <Route path="/admin/academic/timetables" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/admin/academic/calendar" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/admin/academic/subjects" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
      <Route path="/admin/academic/reports" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      
      {/* RFID & Technology */}
      <Route path="/schoolAdmin/rfid-terminals" element={<ProtectedRoute><RFIDTerminalsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/rfid-cards" element={<ProtectedRoute><RFIDCardsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/card-readers" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/pos-terminals" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/hardware-status" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/network" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/device-config" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      
      {/* Super Admin Transport Routes */}
      <Route path="/admin/transport/fleet" element={<ProtectedRoute><FleetOverviewPage /></ProtectedRoute>} />
      <Route path="/admin/transport/routes" element={<ProtectedRoute><RoutePlanningPage /></ProtectedRoute>} />
      <Route path="/admin/transport/trips" element={<ProtectedRoute><TripsPage /></ProtectedRoute>} />
      
      {/* Super Admin RFID Routes */}
      <Route path="/admin/rfid/terminals" element={<ProtectedRoute><RFIDTerminalsPage /></ProtectedRoute>} />
      <Route path="/admin/rfid/readers" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/admin/rfid/cards" element={<ProtectedRoute><RFIDCardsPage /></ProtectedRoute>} />
      <Route path="/admin/rfid/pos-terminals" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/admin/rfid/network" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/admin/rfid/status" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/admin/rfid/firmware" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/admin/rfid/config" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin/rfid/health" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      
      {/* Transport & Safety */}
      <Route path="/schoolAdmin/fleet" element={<ProtectedRoute><FleetOverviewPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/routes" element={<ProtectedRoute><RoutePlanningPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/transport/routes" element={<ProtectedRoute><RoutePlanningPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/trips" element={<ProtectedRoute><TripsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/live-tracking" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/safety" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/emergency-alerts" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/driver-management" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/maintenance" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      
      {/* GPS Fleet Management */}
      <Route path="/schoolAdmin/gps/dashboard" element={<ProtectedRoute><GPSTrackingDashboard /></ProtectedRoute>} />
      <Route path="/schoolAdmin/gps/fleet" element={<ProtectedRoute><GPSFleetPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/gps/geofences" element={<ProtectedRoute><GeofenceManagement /></ProtectedRoute>} />
      <Route path="/schoolAdmin/gps/alarms" element={<ProtectedRoute><GPSTrackingDashboard /></ProtectedRoute>} />
      <Route path="/schoolAdmin/gps/controls" element={<ProtectedRoute><VehicleControlCenter /></ProtectedRoute>} />
      <Route path="/schoolAdmin/gps/reports" element={<ProtectedRoute><GPSReportsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/gps/settings" element={<ProtectedRoute><ProtrackCredentialsPage /></ProtectedRoute>} />

      {/* Library Management */}
      <Route path="/schoolAdmin/library/dashboard" element={<ProtectedRoute><LibraryDashboardPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/library/books" element={<ProtectedRoute><BooksPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/library/borrowings" element={<ProtectedRoute><BorrowingsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/library/fines" element={<ProtectedRoute><FinesPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/library/categories" element={<ProtectedRoute><LibraryCategoriesPage /></ProtectedRoute>} />

      {/* Meals & Cafeteria Management */}
      <Route path="/schoolAdmin/meals/types" element={<ProtectedRoute><MealTypesPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/meals/sessions" element={<ProtectedRoute><MealSessionsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/meals/attendance" element={<ProtectedRoute><MealAttendancePage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/meals/reports" element={<ProtectedRoute><MealReportsPage /></ProtectedRoute>} />

      {/* Wallet Management - School Admin */}
      <Route path="/schoolAdmin/wallet" element={<ProtectedRoute><SchoolAdminWalletDashboard /></ProtectedRoute>} />
      <Route path="/schoolAdmin/wallet/dashboard" element={<ProtectedRoute><SchoolAdminWalletDashboard /></ProtectedRoute>} />
      <Route path="/schoolAdmin/wallet/transactions" element={<ProtectedRoute><SchoolAdminTransactionsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/wallet/credit" element={<ProtectedRoute><CreditWalletPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/wallet/debit" element={<ProtectedRoute><DebitWalletPage /></ProtectedRoute>} />

      {/* Payment Configuration */}
      <Route path="/schoolAdmin/payment-config" element={<ProtectedRoute><PaymentConfigPage /></ProtectedRoute>} />

      {/* Communication Module - School Admin */}
      <Route path="/schoolAdmin/communication/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/communication/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/communication/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

      {/* Legacy communication routes - redirect to new paths */}
      <Route path="/schoolAdmin/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/messages" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/parent-notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      
      {/* Finance & Administration */}
      <Route path="/schoolAdmin/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/invoices/:id" element={<ProtectedRoute><InvoiceDetailPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/billing" element={<ProtectedRoute><BillingManagementPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/fees" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/fee-generation" element={<ProtectedRoute><FeeGenerationPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/payments" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/financial-reports" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/budget" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      
      {/* Reports & Analytics */}
      <Route path="/schoolAdmin/analytics" element={<ProtectedRoute><SchoolAnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/attendance-reports" element={<ProtectedRoute><AttendanceReportsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/performance-reports" element={<ProtectedRoute><PerformanceReportsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/transport-reports" element={<ProtectedRoute><TransportReportsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/safety-reports" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      
      {/* School Services */}
      <Route path="/schoolAdmin/library" element={<ProtectedRoute><LibraryDashboardPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/cafeteria" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/health" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/events" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/gallery" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      
      {/* Settings & Configuration */}
      <Route path="/schoolAdmin/school-profile" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/system-settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/security-settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/backup" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/permissions" element={<ProtectedRoute><SuperAdminsPage /></ProtectedRoute>} />
      <Route path="/schoolAdmin/logs" element={<ProtectedRoute><SystemLogsPage /></ProtectedRoute>} />
      
      {/* Legacy school routes (kept for backwards compatibility) */}
      <Route path="/school/dashboard" element={<ProtectedRoute><SchoolAdminDashboard /></ProtectedRoute>} />
      <Route path="/school/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/school/students" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/school/teachers" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/school/parents" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/school/drivers" element={<ProtectedRoute><DriversPage /></ProtectedRoute>} />
      <Route path="/school/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      
      {/* Teacher routes */}
      <Route path="/teacher/dashboard" element={<ProtectedRoute><TeacherDashboard /></ProtectedRoute>} />
      <Route path="/teacher/lessons" element={<ProtectedRoute><LessonsPage /></ProtectedRoute>} />
      <Route path="/teacher/sessions" element={<ProtectedRoute><SessionsPage /></ProtectedRoute>} />
      <Route path="/teacher/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/teacher/2fa" element={<ProtectedRoute><TwoFactorSettingsPage /></ProtectedRoute>} />
      {/* 
        Removed problematic teacher routes that make unauthorized API calls:
        - /teacher/classes (GroupsPage) - makes admin API calls
        - /teacher/students (UsersPage) - requires admin privileges  
        - /teacher/attendance (AttendancePage) - may make admin API calls
      */}
      
      {/* Parent routes */}
      <Route path="/parent/dashboard" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
      <Route path="/parent/children" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/parent/attendance" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
      <Route path="/parent/transport" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/parent/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/parent/2fa" element={<ProtectedRoute><TwoFactorSettingsPage /></ProtectedRoute>} />

      {/* Parent Wallet */}
      <Route path="/parent/wallet" element={<ProtectedRoute><ParentWalletDashboard /></ProtectedRoute>} />
      <Route path="/parent/wallet/dashboard" element={<ProtectedRoute><ParentWalletDashboard /></ProtectedRoute>} />
      <Route path="/parent/wallet/transactions" element={<ProtectedRoute><ParentTransactionsPage /></ProtectedRoute>} />
      <Route path="/parent/wallet/invoices" element={<ProtectedRoute><ParentInvoicesPage /></ProtectedRoute>} />
      <Route path="/parent/wallet/invoices/:id" element={<ProtectedRoute><InvoiceDetailPage /></ProtectedRoute>} />
      <Route path="/parent/wallet/credits" element={<ProtectedRoute><ParentCreditsPage /></ProtectedRoute>} />
      <Route path="/parent/wallet/debits" element={<ProtectedRoute><ParentDebitsPage /></ProtectedRoute>} />
      <Route path="/parent/wallet/topup" element={<ProtectedRoute><ParentTopUpPage /></ProtectedRoute>} />
      <Route path="/parent/wallet/student-topup" element={<ProtectedRoute><ParentStudentWalletTopUpPage /></ProtectedRoute>} />

      {/* Driver routes */}
      <Route path="/driver/dashboard" element={<ProtectedRoute><DriverDashboard /></ProtectedRoute>} />
      <Route path="/driver/route" element={<ProtectedRoute><DriverRoutesPage /></ProtectedRoute>} />
      <Route path="/driver/routes" element={<ProtectedRoute><DriverRoutesPage /></ProtectedRoute>} />
      <Route path="/driver/students" element={<ProtectedRoute><DriverStudentsPage /></ProtectedRoute>} />
      <Route path="/driver/attendance" element={<ProtectedRoute><DriverStudentsPage /></ProtectedRoute>} />
      <Route path="/driver/trip" element={<ProtectedRoute><DriverDashboard /></ProtectedRoute>} />
      <Route path="/driver/schedule" element={<ProtectedRoute><DriverRoutesPage /></ProtectedRoute>} />
      <Route path="/driver/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/driver/2fa" element={<ProtectedRoute><TwoFactorSettingsPage /></ProtectedRoute>} />
      
      {/* Student routes */}
      <Route path="/student/dashboard" element={<ProtectedRoute><StudentDashboard /></ProtectedRoute>} />
      <Route path="/student/classes" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
      <Route path="/student/schedule" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/student/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/student/2fa" element={<ProtectedRoute><TwoFactorSettingsPage /></ProtectedRoute>} />

      {/* Student Wallet */}
      <Route path="/student/wallet" element={<ProtectedRoute><StudentWalletDashboard /></ProtectedRoute>} />
      <Route path="/student/wallet/dashboard" element={<ProtectedRoute><StudentWalletDashboard /></ProtectedRoute>} />
      <Route path="/student/wallet/transactions" element={<ProtectedRoute><StudentTransactionsPage /></ProtectedRoute>} />
      <Route path="/student/wallet/credits" element={<ProtectedRoute><StudentCreditsPage /></ProtectedRoute>} />
      <Route path="/student/wallet/debits" element={<ProtectedRoute><StudentDebitsPage /></ProtectedRoute>} />

      {/* Global Notifications Route (all roles) */}
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

      {/* Catch all route */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AppRoutes />
      </NotificationProvider>
    </AuthProvider>
  )
}

export default App